import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import validator from 'validator';
import { useTranslation } from 'react-i18next';
import { phoneNrValidator, preventNonPhoneCharacters } from '../../../Utils';
import { usersErrorMsg } from '../../../Utils/errorMessages';
import {
  useCreateProviderMutation,
  useCreateProviderPropertiesMutation,
  useDeleteProviderMutation,
  useDeleteProviderPropertiesMutation,
  useUpdateProviderMutation,
  useUpdateProviderPropertiesMutation
} from '../../../redux/api/providers';
import LoadingAnimation from '../../Utils/BateryChargeAnimation';
import Button from '../../Utils/Button';
import FormInput from '../../Utils/Form/FormInput';
import inputTypes from '../../Utils/Form/FormInput/inputTypes';
import IfRender from '../../Utils/IfRender';
import ModalFrame from '../ModalFrame';
import './style.scss';
import ImageUpload from '../../ImageUpload/index.jsx';

export default function ProviderModal({
  modalData = {},
  toggleModal = () => {},
  providerTypeId = 0,
  refetch
}) {
  const [providerId, setProviderId] = useState(providerTypeId);
  const [name, setName] = useState(modalData.name || '');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState(modalData?.organizationProperties?.contact_email || '');
  const [emailError, setEmailError] = useState('');
  const [phone, setPhone] = useState(modalData?.organizationProperties?.contact_phone || '');
  const [properties, setProperties] = useState(modalData?.organizationProperties);
  const [phoneError, setPhoneError] = useState('');
  const [providerImage, setProviderImage] = useState(
    modalData?.organizationProperties?.provider_logo || ''
  );
  const [canSubmit, setCanSubmit] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [isEditModal] = useState(Object.entries(modalData).length > 0);
  const [generalError, setGeneralError] = useState('');

  const { t } = useTranslation();
  const { users = [], charge_points: chargingPoints = [] } = modalData;
  const [
    deleteProvider,
    {
      isLoading: isDeleteLoading,
      isError: isDeleteError,
      error: deleteError,
      isSuccess: isDeleteSuccess
    }
  ] = useDeleteProviderMutation();

  const [
    createProvider,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      isSuccess: isCreateSuccess,
      error: createError
    }
  ] = useCreateProviderMutation();

  const [
    updateProvider,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess
    }
  ] = useUpdateProviderMutation();
  const [createProviderPropreties, {}] = useCreateProviderPropertiesMutation();
  const [deleteProviderPropreties, {}] = useDeleteProviderPropertiesMutation();
  const [updateProviderProperties, {}] = useUpdateProviderPropertiesMutation();
  const inputs = {
    name: 'provider_name',
    email: 'contact_email',
    phone: 'contact_phone',
    img: 'company_image'
  };

  const validate = {
    provider_name: (value) => value.length >= 3 && value.length <= 20,
    contact_email: validator.isEmail,
    contact_phone: (value) => !value || phoneNrValidator(value.trim()),
    provider_logo: (value) => true
  };

  const validationRef = useRef({
    provider_name: validate.provider_name(name),
    contact_phone: validate.contact_phone(phone),
    contact_email: validate.contact_email(email),
    provider_logo: validate.provider_logo(providerImage)
  }); //keep track of all validation fields here. Only required fields should be added here

  const setErrorMsg = {
    provider_name: setNameError,
    contact_email: setEmailError,
    contact_phone: setPhoneError,
    provider_logo: setLogoError
  };

  const sendData = ({ data, id: orgId }) => {
    if (data.provider_name && data.provider_name !== modalData.name) {
      updateProvider({
        data: { name: data.provider_name },
        id: orgId
      });
    } else if (name && name !== modalData.name) {
      updateProvider({
        data: { name },
        id: orgId
      });
    }

    // Handle provider_id change if present
    if (data.provider_id && String(data.provider_id) !== String(modalData.provider_id)) {
      updateProvider({
        data: { provider_id: data.provider_id },
        id: orgId
      });
    }

    // Process other properties
    for (const key in data) {
      // Skip name which is handled separately at organization level
      if (key === 'provider_name' || key === 'name') {
        continue;
      }

      if (Object.hasOwnProperty.call(data, key)) {
        const value = data[key];
        const originalValue =
          modalData.organizationProperties && modalData.organizationProperties[key];
        const propertyId =
          modalData.organizationProperties && modalData.organizationProperties[key + '_id'];

        // Handle empty string (delete property)
        if (typeof value === 'string' && value === '' && propertyId) {
          deleteProviderPropreties(propertyId);
          continue;
        }

        // If value changed and property exists, update it
        if (value !== originalValue && propertyId) {
          updateProviderProperties({
            data: { propertyName: key, propertyValue: value },
            id: propertyId
          });
        }
        // If value changed and property doesn't exist, create it
        else if (value !== originalValue && !propertyId && value) {
          createProviderPropreties({
            data: { propertyName: key, propertyValue: value },
            orgId
          });
        }
      }
    }
    window.location.reload();
    // Close modal after processing
    toggleModal();
  };

  const handleSubmit = async () => {
    if (isEditModal) {
      sendData({
        id: modalData.id,
        data: {
          contact_email: email,
          contact_phone: phone,
          provider_logo: providerImage
        }
      });
    } else {
      try {
        // Create the provider first
        const organization = await createProvider({
          name,
          organizationTypeId: providerId
        });

        if (organization?.data?.id) {
          // Create properties for the new provider
          await sendData({
            id: organization.data.id,
            data: {
              contact_email: email,
              contact_phone: phone,
              provider_logo: providerImage
            }
          });
        }
      } catch (error) {
        console.error('Error creating provider:', error);
        setGeneralError(error.message || 'Failed to create provider');
      }
    }
    refetch();
  };

  const onBlur = (e) => {
    const { id, value } = e.target;
    setInputValue[id](value.trim());
  };

  const handleCanSubmit = () => {
    if (Object.values(validationRef.current).every((value) => value === true))
      //only enable canSubmit if all validations are true
      setCanSubmit(true);
    else setCanSubmit(false);
  };

  const setInputValue = {
    provider_name: setName,
    contact_email: setEmail,
    contact_phone: setPhone,
    provider_logo: setProviderImage
  };

  const handleValidation = (id, value) => {
    const isValid = validate[id](value);
    validationRef.current[id] = isValid;
    !isValid ? setErrorMsg[id](t(usersErrorMsg[id])) : setErrorMsg[id]('');
    handleCanSubmit();
  };

  const onChange = (e) => {
    const { id, value } = e.target;
    setInputValue[id](value);
    handleValidation(id, value);
  };

  useEffect(() => {
    isDeleteError && setGeneralError(deleteError.data.error.message);
    isUpdateError && setGeneralError(updateError.data.error.message);
    isCreateError &&
      setGeneralError(
        createError?.data?.error?.message?.includes('unique_organization_name')
          ? t('organization_unique_name')
          : createError?.data?.error?.message || t('generic_error_message')
      );
    (isUpdateSuccess || isDeleteSuccess || isCreateSuccess) && toggleModal();
  }, [
    isUpdateSuccess,
    isDeleteSuccess,
    isCreateSuccess,
    isCreateError,
    isDeleteError,
    isUpdateError
  ]);

  const renderTextInput = () => (
    <>
      <FormInput
        id='provider_name'
        required={Object.keys(validationRef.current).includes(inputs.name)}
        label={t('provider_name')}
        value={name}
        placeholder={t('type_the_provider_name')}
        errorMessage={nameError}
        onBlur={onBlur}
        onChange={onChange}
      />
      <FormInput
        id={'contact_email'}
        type={inputTypes.email}
        label={t('contact_email')}
        value={email}
        placeholder={t('type_the_contact_email')}
        errorMessage={emailError}
        onBlur={onBlur}
        onChange={onChange}
      />
      <FormInput
        id={'contact_phone'}
        type={inputTypes.tel}
        label={t('contact_phone_number')}
        value={phone}
        placeholder='Ex: +1234 567 890'
        errorMessage={phoneError}
        onBeforeInput={preventNonPhoneCharacters}
        onBlur={onBlur}
        onChange={onChange}
      />
      <ImageUpload
        id={'provider_logo'}
        name={'provider_logo'}
        providerImage={providerImage}
        setImage={setProviderImage}
        onChange={onChange}
      />
    </>
  );

  return (
    <ModalFrame
      onClose={toggleModal}
      bodyClass='modal__body--organization'
      closeOnBgClick={!isDeleteLoading || !isUpdateLoading || !isCreateLoading}>
      <IfRender
        condition={!isDeleteLoading || !isUpdateLoading || !isCreateLoading}
        otherwise={<LoadingAnimation dark />}>
        <form id='edit-user' className='form'>
          <div className='form__section-title modal-form__header'>
            <h1>{isEditModal ? t('edit_provider') : t('add_provider')}</h1>
            <Button
              className='button__icon color--alfa-foreground'
              iconName='close'
              onClick={toggleModal}
            />
          </div>
          {renderTextInput()}
          <div className='form__section-footer'>
            <Button
              className='button__primary'
              label={t('save')}
              disabled={!canSubmit}
              onClick={handleSubmit}
            />
            {isEditModal && (
              <Button
                className='button__secondary'
                label={t('delete')}
                type='button'
                onClick={() => {
                  confirm(t('provider_deletion_first_confirmation')) &&
                    confirm(t('provider_deletion_second_confirmation')) &&
                    confirm(t('provider_deletion_third_confirmation')) &&
                    deleteProvider(modalData.id);
                }}
              />
            )}
          </div>
          {generalError && <p className='form__section-error'>{generalError}</p>}
        </form>
      </IfRender>
    </ModalFrame>
  );
}

ProviderModal.propTypes = {
  toggleModal: PropTypes.func,
  modalData: PropTypes.object
};
